/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2021, 2025
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/

import { ApiStatus } from '../../../../ts';
import { Account } from '../../../../ts/account';
import { InstanceState } from '../../../../ts/instance';
import { AsyncAppThunk, SessionUser } from '../../../../ts/state';
import * as accountsAPI from '../../api/accounts';
import * as API from '../../api/session';
import * as types from './actionTypes';

export const setSession = (
  user: SessionUser,
  region: string,
  instance: string,
  account: Account,
  workspaceId: string,
  skillId: string,
  instanceState: InstanceState,
  underReadOnlyView: boolean,
  assistantId: string
) => {
  return {
    type: types.CREATE_SESSION,
    user,
    region,
    instance,
    account,
    workspaceDetail: { workspace_id: workspaceId },
    workspaceId,
    skillId,
    instanceState,
    underReadOnlyView,
    assistantId
  };
};

export const destroySession = () => {
  return {
    type: types.DESTROY_SESSION
  };
};

export const resetSession = () => {
  return {
    type: types.RESET_SESSION
  };
};

export const setWorkspace = (workspaceId: string) => {
  return {
    type: types.SET_WORKSPACE,
    workspaceId
  };
};

export const setWorkspaceDetail = (workspaceDetail = {}) => ({
  type: types.SET_WORKSPACE_DETAIL,
  workspaceDetail
});

export const setUser = user => {
  return {
    type: types.SET_USER,
    user
  };
};

export const setTenant = (region: string, instance: string) => {
  return {
    type: types.SET_TENANT,
    region,
    instance
  };
};

export const setAccount = account => {
  return {
    type: types.SET_ACCOUNT,
    account
  };
};

export const setInstanceState = (instanceState: InstanceState) => {
  return {
    type: types.SET_INSTANCE_STATE,
    instanceState
  };
};

export const setSkillId = (skillId: string) => ({
  type: types.SET_SKILL_ID,
  skillId
});

export const setAssistantId = (assistantId: string) => ({
  type: types.SET_ASSISTANT_ID,
  assistantId
});

export const setReadOnly = (underReadOnlyView: boolean) => {
  return {
    type: types.SET_READ_ONLY,
    underReadOnlyView
  };
};

export const createSession = (
  user: SessionUser,
  region: string,
  instance: string,
  account: Account,
  workspaceId: string,
  skillId: string,
  underReadOnlyView: boolean,
  assistantId: string
): AsyncAppThunk => {
  return (dispatch, getState) => {
    const session = getState().session;
    const instanceState =
      session.region !== region || session.instance !== instance
        ? undefined
        : session.instanceState;
    dispatch(
      setSession(
        user,
        region,
        instance,
        account,
        workspaceId,
        skillId,
        instanceState,
        underReadOnlyView,
        assistantId
      )
    );
    if (user && region && instance) {
      dispatch(updateInstanceState(region, instance));
    }
  };
};

export const updateTenant = (
  region: string,
  instance: string
): AsyncAppThunk => {
  return (dispatch, getState) => {
    const { session } = getState();
    if (session.region !== region || session.instance !== instance) {
      dispatch(setTenant(region, instance));
      dispatch(updateInstanceState(region, instance));
    }
  };
};

export const updateInstanceState = (
  region: string,
  instance: string
): AsyncAppThunk => {
  return async dispatch => {
    const instanceState = await API.getInstanceState(region, instance);
    dispatch(setInstanceState(instanceState));
  };
};

export const getAccount = (accountId: string): AsyncAppThunk => {
  return async dispatch => {
    if (accountId) {
      const account = await accountsAPI.getAccount(accountId);
      dispatch(setAccount(account));
    }
  };
};

export const fetchUserStart = () => ({
  type: types.FETCH_USER_START
});

export const fetchUserSuccess = user => ({
  type: types.FETCH_USER_SUCCESS,
  payload: user
});

export const fetchUserError = error => ({
  type: types.FETCH_USER_ERROR,
  payload: {
    message: error.message,
    status: ApiStatus.ERROR
  }
});

export const sessionInactive = () => ({
  type: types.SESSION_INACTIVE
});

export const sessionRevoked = () => ({
  type: types.SESSION_REVOKED
});

export const ipAddressRestriction = () => ({
  type: types.IP_ADDRESS_RESTRICTION
});
